// UPDATED BY GEMINI - MASSIVE MODAL, CLEAR BUTTON, AND ULTIMATE ANDROID PRINT FIX
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Eye, Edit, Trash2, Printer, RefreshCw, Package, ChevronDown, XCircle } from 'lucide-react';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DoubleScrollbar from '../../components/UI/DoubleScrollbar';
import toast from 'react-hot-toast';
import { ErrorHandler } from '../../utils/errorHandler';
import Modal from '../../components/UI/Modal';
import Input from '../../components/UI/Input';
import Button from '../../components/UI/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import JsBarcode from 'jsbarcode';

interface Employee { _id: string; name: string; designation?: string; position?: string; email?: string; }
interface Customer { _id: string; name: string; address?: string; phone?: string; email?: string; }
interface Project { _id: string; name: string; customerName?: string; }
interface Product { _id: string; name: string; barcode?: string; unit?: string; sku?: string; description?: string; category?: any; customFields?: Record<string, string>; specifications?: any; units?: string[]; price?: number; costPrice?: number; }
interface RequisitionItem { product?: string | null; barcode?: string; name: string; brand?: string; model?: string; sku?: string; type?: string; compressorType?: string; countryOfOrigin?: string; countryOfAssembly?: string; details?: string; description?: string; capacity?: string; specification?: string; serialNo?: string; partNo?: string; unit: string; quantity: number; unitPrice: number; discount: number; remarks?: string; }
interface Requisition { _id: string; requisitionNumber: string; date: string; employee: Employee | string; employeeName: string; employeeDesignation?: string; customer: Customer | string; customerName: string; project?: Project | string | null; projectName?: string; status: string; items: (RequisitionItem & { product?: { _id: string } | null })[]; notes?: string; createdBy: { _id: string; firstName?: string; lastName?: string } | string; createdAt: string; }

function getTodayDateString() { return new Date().toISOString().split('T')[0]; }
function safeCurrency(val: number | undefined | null): string { const n = typeof val === 'number' && !Number.isNaN(val) ? val : 0; return '৳ ' + n.toLocaleString('en-BD', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function formatDateDMY(dateInput: string | Date | undefined | null): string {
  if (!dateInput) return '-';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '-';
  const day = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-GB', { month: 'short' });
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function numberToWords(num: number): string {
  if (!Number.isFinite(num) || num <= 0) return 'zero';
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  const twoDigits = (n: number): string => { if (n < 20) return ones[n]; const t = Math.floor(n / 10); const o = n % 10; return tens[t] + (o ? ' ' + ones[o] : ''); };
  const threeDigits = (n: number): string => { let str = ''; if (n > 99) { str += ones[Math.floor(n / 100)] + ' hundred'; n = n % 100; if (n) str += ' '; } if (n) str += twoDigits(n); return str; };
  const integer = Math.floor(num); if (integer === 0) return 'zero';
  const crore = Math.floor(integer / 10000000); const lakh = Math.floor((integer % 10000000) / 100000); const thousand = Math.floor((integer % 100000) / 1000); const hundred = integer % 1000;
  const parts: string[] = []; if (crore) parts.push(threeDigits(crore) + ' crore'); if (lakh) parts.push(threeDigits(lakh) + ' lac'); if (thousand) parts.push(threeDigits(thousand) + ' thousand'); if (hundred) parts.push(threeDigits(hundred)); return parts.join(' ');
}

function takaInWords(amount: number): string {
  if (!amount || amount <= 0) return 'Zero Taka only';
  const integer = Math.floor(amount); const paise = Math.round((amount - integer) * 100);
  const takaWordsRaw = numberToWords(integer); const takaWords = takaWordsRaw.split(' ').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : '')).join(' ') || 'Zero';
  let result = takaWords + " Taka";
  if (paise > 0) { const paiseWordsRaw = numberToWords(paise); const paiseWords = paiseWordsRaw.split(' ').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : '')).join(' '); result += " and " + paiseWords + " Paisa"; }
  return result + ' Only';
}

function computeRequisitionTotals(items: any[]) { const totalAmount = (items || []).reduce((sum, it) => sum + ((it.quantity || 0) * (it.unitPrice || 0)), 0); const totalDiscount = (items || []).reduce((sum, it) => sum + (it.discount || 0), 0); const grandTotal = totalAmount - totalDiscount; return { totalAmount, totalDiscount, grandTotal }; }

const RequisitionsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [requisitions, setRequisitions] = useState<Requisition[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingRequisition, setEditingRequisition] = useState<Requisition | null>(null);
  const [viewRequisition, setViewRequisition] = useState<Requisition | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [parentCategories, setParentCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [selectedParentCategory, setSelectedParentCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [form, setForm] = useState({ date: getTodayDateString(), employeeId: '', employeeName: '', designation: '', customerId: '', projectId: '', notes: '', status: 'draft' });
  const [addedItems, setAddedItems] = useState<RequisitionItem[]>([]);
  const [currentItem, setCurrentItem] = useState<RequisitionItem>({ product: undefined, name: '', unit: '', quantity: 1, unitPrice: 0, discount: 0, remarks: '', brand: '', model: '', sku: '', type: '', compressorType: '', countryOfOrigin: '', countryOfAssembly: '', details: '', description: '', capacity: '', specification: '', serialNo: '', partNo: '' });
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownPortalRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [editProductIdx, setEditProductIdx] = useState<number | null>(null);
  const [editProductData, setEditProductData] = useState<any>(null);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalType, setCreateModalType] = useState('');
  const [createModalData, setCreateModalData] = useState<any>({ name: '', description: '', website: '', code: '', region: '' });
  const [newCustomer, setNewCustomer] = useState({ name: '', email: '', phone: '', address: '' });
  const [newProject, setNewProject] = useState({ name: '', customerId: '', customerName: '', description: '' });
  const [newProduct, setNewProduct] = useState({ productName: '', location: '', category: '', brand: '', model: '', sku: '', type: '', compressorType: '', countryOfOrigin: '', countryOfAssembly: '', specification: '', serialNo: '', partNo: '', details: '', description: '', price: '', costPrice: '', stockQuantity: '', minStockLevel: '', unitIds: [] as string[], unitId: '', image: '', generatedBarcode: '' });
  const [locations, setLocations] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [compressorTypes, setCompressorTypes] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [rootCategories, setRootCategories] = useState<any[]>([]);
  const [newProductErrors, setNewProductErrors] = useState<Record<string, string>>({});
  const [unitDropdownOpen, setUnitDropdownOpen] = useState(false);
  const unitDropdownRef = useRef<HTMLDivElement>(null);
  const barcodeCanvasRef = useRef<HTMLCanvasElement>(null);
  const [barcodeImage, setBarcodeImage] = useState<string | null>(null);

  const updateDropdownPosition = () => {
    if (searchInputRef.current) {
      const rect = searchInputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownMaxHeight = 240;
      const showAbove = spaceBelow < 180 && spaceAbove > spaceBelow;

      setDropdownPosition({
        top: showAbove ? Math.max(10, rect.top - Math.min(dropdownMaxHeight, spaceAbove - 10)) : rect.bottom + 2,
        left: rect.left,
        width: Math.max(rect.width, 240)
      });
    }
  };

  useEffect(() => { fetchRequisitions(); fetchInitialData(); }, []);

  // Keep dropdown aligned on scroll or resize
  useEffect(() => {
    if (!showProductDropdown) return;
    updateDropdownPosition();

    const handleScrollOrResize = () => {
      updateDropdownPosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [showProductDropdown]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        searchInputRef.current && !searchInputRef.current.contains(target) &&
        dropdownPortalRef.current && !dropdownPortalRef.current.contains(target)
      ) {
        setShowProductDropdown(false);
      }
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(target)) {
        setUnitDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchRequisitions = async (force = false) => {
    try { setLoading(true); const res = await apiService.get('/requisitions', force ? { forceRefresh: true } : undefined); setRequisitions(res.data.requisitions || []); }
    catch (error) { toast.error('Failed to fetch requisitions'); } finally { setLoading(false); }
  };

  const fetchInitialData = async () => {
    try {
      const [empRes, custRes, prodRes, catRes, locRes, brandRes, typeRes, compRes, countryRes, unitRes, projRes, rootCatRes] = await Promise.all([
        apiService.get('/employees'),
        apiService.get('/customers'),
        apiService.get('/products'),
        apiService.get('/categories/all'),
        apiService.get('/locations/all'),
        apiService.get('/brands/all'),
        apiService.get('/product-types/all'),
        apiService.get('/compressor-types/all'),
        apiService.get('/countries/all'),
        apiService.get('/units/all'),
        apiService.get('/projects?limit=1000'),
        apiService.get('/categories/root')
      ]);
      setEmployees(empRes.data.employees || []);
      setCustomers(custRes.data.customers || []);
      setProducts(prodRes.data.products || []);
      setLocations(locRes.data || []);
      setBrands(brandRes.data || []);
      setTypes(typeRes.data || []);
      setCompressorTypes(compRes.data || []);
      setCountries(countryRes.data || []);
      setUnits(unitRes.data || []);
      setProjects(projRes.data.projects || []);
      setRootCategories(rootCatRes.data || []);
      const allCats = catRes.data || [];
      setParentCategories(allCats.filter((c: any) => !c.parentCategory));
      setSubCategories(allCats.filter((c: any) => c.parentCategory));
    } catch (error) {
      console.error('Initial data fetch error:', error);
    }
  };

  const projectsForCustomer = form.customerId ? projects.filter((p: any) => { const cust = customers.find(c => c._id === form.customerId); return cust && p.customerName === cust.name; }) : [];

  const handleCustomerChange = (cid: string) => { setForm((p) => ({ ...p, customerId: cid, projectId: '' })); };

  const handleCreateCustomer = async () => {
    if (!newCustomer.name) { toast.error('Customer name is required'); return; }
    try {
      const res = await apiService.post('/customers', newCustomer);
      toast.success('Customer created successfully');
      const createdCustomer = res.data.customer || res.data;
      if (createdCustomer && createdCustomer._id) {
        setCustomers(prev => [...prev, createdCustomer]);
        setForm(prev => ({ ...prev, customerId: createdCustomer._id }));
      } else { fetchInitialData(); }
      setShowCustomerModal(false);
      setNewCustomer({ name: '', email: '', phone: '', address: '' });
    } catch (error: any) { ErrorHandler.showError(error); }
  };

  const handleCreateProject = async () => {
    if (!newProject.name) { toast.error('Project name is required'); return; }
    if (!form.customerId) { toast.error('Please select a customer first'); return; }
    const customer = customers.find(c => c._id === form.customerId);
    try {
      const payload = { ...newProject, customerId: form.customerId, customerName: customer?.name };
      const res = await apiService.post('/projects', payload);
      toast.success('Project created successfully');
      setProjects(prev => [...prev, res.data.project]);
      setForm(prev => ({ ...prev, projectId: res.data.project._id }));
      setShowProjectModal(false);
      setNewProject({ name: '', customerId: '', customerName: '', description: '' });
    } catch (error: any) { ErrorHandler.showError(error); }
  };

  const handleCreateEntity = (type: string) => {
    setCreateModalType(type);
    setCreateModalData({ name: '', description: '', website: '', code: '', region: '' });
    setShowCreateModal(true);
  };

  const handleCreateEntitySubmit = async () => {
    try {
      let endpoint = '';
      const data: any = { name: createModalData.name };
      switch (createModalType) {
        case 'location': endpoint = '/locations'; data.description = createModalData.description; break;
        case 'category': endpoint = '/categories'; data.description = createModalData.description; break;
        case 'brand': endpoint = '/brands'; data.description = createModalData.description; data.website = createModalData.website; break;
        case 'type': endpoint = '/product-types'; data.description = createModalData.description; break;
        case 'compressorType': endpoint = '/compressor-types'; data.description = createModalData.description; break;
        case 'country': endpoint = '/countries'; data.description = createModalData.description; data.code = createModalData.code; data.region = createModalData.region; break;
        case 'unit': endpoint = '/units'; data.description = createModalData.description; break;
        default: return;
      }
      await apiService.post(endpoint, data);
      toast.success(`${createModalType.charAt(0).toUpperCase() + createModalType.slice(1)} created successfully`);
      setShowCreateModal(false);
      fetchInitialData();
    } catch (error) { toast.error(`Failed to create ${createModalType}`); }
  };

  const handleParentCategoryChange = async (parentCategoryId: string) => {
    setSelectedParentCategory(parentCategoryId);
    setSelectedSubCategory('');
    if (parentCategoryId) {
      try {
        const response = await apiService.get(`/categories/subcategories/${parentCategoryId}`);
        setSubCategories(response.data || []);
      } catch (error) { console.error('Error fetching subcategories:', error); }
    } else { setSubCategories([]); }
    setNewProduct(prev => ({ ...prev, category: '' }));
  };

  const updateBarcodeFn = (updated: any) => {
    setNewProduct(updated); if (updated.brand && updated.model && updated.type && updated.compressorType) {
      const brandObj = brands.find(b => b._id === updated.brand);
      const typeObj = types.find(t => t._id === updated.type);
      const compObj = compressorTypes.find(c => c._id === updated.compressorType);
      const brandName = brandObj ? brandObj.name : '';
      const typeName = typeObj ? typeObj.name : '';
      const compName = compObj ? compObj.name : '';
      const bc = (brandName + "-" + updated.model + "-" + typeName + "-" + compName).replace(/\s+/g, '').toUpperCase();
      setNewProduct(prev => ({ ...prev, generatedBarcode: bc })); if (barcodeCanvasRef.current) { try { JsBarcode(barcodeCanvasRef.current, bc, { format: 'CODE128', width: 2, height: 100, displayValue: true }); setBarcodeImage(barcodeCanvasRef.current.toDataURL()); } catch (e) { console.error('Barcode generation error:', e); } }
    }
  };

  const airconCategories = ['Air-Conditioner', 'VRF', 'Chiller', 'Split Type'];
  const selectedParentCategoryObj = rootCategories.find(c => c._id === selectedParentCategory);
  const isAirConditioner = selectedParentCategoryObj ? airconCategories.includes(selectedParentCategoryObj.name) : false;

  const handleAddNewProduct = async () => {
    const errors: Record<string, string> = {};
    if (!newProduct.productName?.trim()) errors.productName = 'Product name is required';
    if (!selectedParentCategory) errors.parentCategory = 'Parent category is required';
    if (!newProduct.category) errors.category = 'Sub category is required';
    if (!newProduct.unitIds || newProduct.unitIds.length === 0) errors.unitIds = 'Units are required';

    if (Object.keys(errors).length > 0) {
      setNewProductErrors(errors);
      toast.error('Please fill all mandatory fields');
      return;
    }
    setNewProductErrors({});

    try {
      const payload = { 
        name: newProduct.productName, 
        location: newProduct.location,
        category: newProduct.category,
        brand: newProduct.brand, 
        model: newProduct.model, 
        sku: newProduct.sku,
        type: newProduct.type,
        compressorType: newProduct.compressorType,
        countryOfOrigin: newProduct.countryOfOrigin,
        countryOfAssembly: newProduct.countryOfAssembly,
        specification: newProduct.specification,
        serialNo: newProduct.serialNo,
        partNo: newProduct.partNo,
        details: newProduct.details,
        description: newProduct.description,
        price: Number(newProduct.price) || 0,
        costPrice: Number(newProduct.costPrice) || 0,
        stockQuantity: Number(newProduct.stockQuantity) || 0,
        minStockLevel: Number(newProduct.minStockLevel) || 0,
        units: newProduct.unitIds,
        barcode: newProduct.generatedBarcode 
      };
      const res = await apiService.post('/products', payload); 
      toast.success('Product created'); 
      setShowAddProductModal(false); 
      setNewProductErrors({}); 
      fetchInitialData(); 
      handleProductSelect(res.data.product);
    } catch (e: any) { 
      ErrorHandler.showError(e); 
    }
  };

  const handleProductSelect = (p: Product) => {
    const cf = (p as any).customFields || {};
    setCurrentItem({ product: p._id, barcode: p.barcode || '', name: p.name, brand: cf.brand || '', model: cf.model || '', sku: p.sku || '', type: cf.type || '', compressorType: cf.compressorType || '', countryOfOrigin: cf.countryOfOrigin || '', countryOfAssembly: cf.countryOfAssembly || '', details: cf.details || '', description: p.description || cf.description || '', capacity: cf.capacity || '', specification: typeof cf.specifications === 'string' ? cf.specifications : (cf.specifications ? JSON.stringify(cf.specifications) : ''), serialNo: cf.serialNo || '', partNo: cf.partNo || '', unit: (p as any).units?.length ? (p as any).units[0] : (p.unit || 'pcs'), quantity: 1, unitPrice: 0, discount: 0, remarks: '' });
    setProductSearchTerm(p.name); setShowProductDropdown(false);
  };

  const handleAddItem = () => {
    if (!currentItem.product && !currentItem.name) { toast.error('Please select a product'); return; }
    if (!currentItem.quantity || currentItem.quantity <= 0) { toast.error('Quantity must be greater than 0'); return; }
    setAddedItems((p) => [...p, { ...currentItem }]);
    setCurrentItem({ product: undefined, name: '', unit: '', quantity: 1, unitPrice: 0, discount: 0, remarks: '', brand: '', model: '', sku: '', type: '', compressorType: '', countryOfOrigin: '', countryOfAssembly: '', details: '', description: '', capacity: '', specification: '', serialNo: '', partNo: '' });
    setProductSearchTerm(''); setSelectedParentCategory(''); setSelectedSubCategory('');
  };

  const handleRemoveItem = (idx: number) => { setAddedItems((p) => p.filter((_, i) => i !== idx)); setSelectedRows((p) => p.filter((i) => i !== idx).map((i) => (i > idx ? i - 1 : i))); };
  const handleSelectRow = (idx: number) => { setSelectedRows((p) => (p.includes(idx) ? p.filter((i) => i !== idx) : [...p, idx])); };
  const handleBulkDelete = () => { if (selectedRows.length === 0) return; if (!window.confirm("Remove " + selectedRows.length + " items?")) return; setAddedItems((p) => p.filter((_, i) => !selectedRows.includes(i))); setSelectedRows([]); };
  const handleEditItem = (idx: number) => { setEditProductIdx(idx); setEditProductData({ ...addedItems[idx] }); };
  const handleSaveEditItem = () => { if (editProductIdx === null || !editProductData) return; setAddedItems((prev) => prev.map((it, i) => (i === editProductIdx ? editProductData : it))); setEditProductIdx(null); setEditProductData(null); };

  const handleSave = async () => {
    if (!form.customerId) { toast.error('Please select a customer'); return; }
    if (addedItems.length === 0) { toast.error('Please add at least one product'); return; }
    try {
      setSaving(true); const payload = { date: form.date, customer: form.customerId, project: form.projectId || undefined, notes: form.notes || undefined, status: form.status, items: addedItems };
      if (!form.employeeId) { payload['employeeName' as keyof typeof payload] = form.employeeName as any; payload['employeeDesignation' as keyof typeof payload] = form.designation as any; } else { payload['employee' as keyof typeof payload] = form.employeeId as any; }
      if (showEditModal && editingRequisition) { await apiService.put("/requisitions/" + editingRequisition._id, payload); toast.success('Requisition updated successfully'); }
      else { await apiService.post('/requisitions', payload); toast.success('Requisition created successfully'); }
      await fetchRequisitions(true); closeModals();
    } catch (error: any) { ErrorHandler.showError(error); } finally { setSaving(false); }
  };

  const closeModals = () => { setShowAddModal(false); setShowEditModal(false); setEditingRequisition(null); setForm({ date: getTodayDateString(), employeeId: '', employeeName: '', designation: '', customerId: '', projectId: '', notes: '', status: 'draft' }); setAddedItems([]); setSelectedRows([]); setProductSearchTerm(''); setSelectedParentCategory(''); setSelectedSubCategory(''); };

  const openAdd = () => {
    closeModals(); const name = ((user?.firstName || '') + " " + (user?.lastName || '')).trim(); const designation = user?.designation || '';
    const matchingEmp = employees.find(e => e.name === name);
    setForm({ date: getTodayDateString(), employeeId: matchingEmp?._id || '', employeeName: name, designation, customerId: '', projectId: '', notes: '', status: 'draft' }); setShowAddModal(true);
  };

  const openEdit = async (r: Requisition) => {
    try {
      const res = await apiService.get("/requisitions/" + r._id); const full = res.data;
      setEditingRequisition(full);
      const eId = typeof full.employee === 'object' ? full.employee?._id : full.employee;
      const cId = typeof full.customer === 'object' ? full.customer?._id : full.customer;
      const pId = typeof full.project === 'object' ? full.project?._id : full.project;
      setForm({ date: full.date.split('T')[0], employeeId: eId || '', employeeName: full.employeeName || '', designation: full.employeeDesignation || '', customerId: cId || '', projectId: pId || '', notes: full.notes || '', status: full.status || 'draft' });
      setAddedItems(full.items.map((it: any) => ({ ...it, product: it.product?._id || it.product })));
      setShowEditModal(true);
    } catch (error: any) { ErrorHandler.showError(error); }
  };

  const openView = async (r: Requisition) => {
    try { const res = await apiService.get("/requisitions/" + r._id); setViewRequisition(res.data); setShowViewModal(true); }
    catch (error: any) { ErrorHandler.showError(error); }
  };

  const handleDelete = async (r: Requisition) => {
    if (!window.confirm("Are you sure you want to delete this requisition?")) return;
    try { await apiService.delete("/requisitions/" + r._id); toast.success('Requisition deleted'); fetchRequisitions(true); }
    catch (error: any) { ErrorHandler.showError(error); }
  };

  const handlePrint = async (r: Requisition) => {
    try {
      const res = await apiService.get("/requisitions/" + r._id);
      const fullReq = res.data;
      const dateStr = formatDateDMY(fullReq.date);
      const totals = computeRequisitionTotals(fullReq.items);
      
      const content = `<!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Print Requisition - ${fullReq.requisitionNumber}</title>
          <style>
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              @page { margin: 0.5cm; size: auto; }
              .no-print { display: none; }
            }
            body { 
              font-family: 'Times New Roman', serif; 
              padding: 0; 
              margin: 0;
              color: #000; 
              line-height: 1.4;
              background: white;
            }
            .container { padding: 10px 20px; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .font-bold { font-weight: bold; }
            .uppercase { text-transform: uppercase; }
            
            .header-title { font-size: 20px; font-weight: bold; margin: 0; }
            .header-address { font-size: 14px; margin: 2px 0; }
            .doc-title { 
              font-size: 16px; 
              font-weight: bold; 
              border-top: 1px solid #000; 
              border-bottom: 1px solid #000; 
              padding: 4px 0; 
              margin: 8px 0 16px 0; 
              text-transform: uppercase;
            }

            .info-grid { 
              display: flex; 
              justify-content: space-between; 
              font-size: 13px; 
              margin-bottom: 15px;
            }
            .info-col { display: flex; flex-direction: column; gap: 2px; }

            table { 
              width: 100%; 
              border-collapse: collapse; 
              font-size: 11px; 
              margin-top: 10px;
            }
            th, td { 
              border: 1px solid #000; 
              padding: 6px 8px; 
            }
            th { 
              background-color: #e2e8f0 !important; 
              font-weight: bold; 
              text-transform: uppercase;
              text-align: center;
            }
            
            .product-name { font-weight: bold; font-size: 12px; }
            .product-details { 
              font-size: 9px; 
              color: #374151; 
              display: flex; 
              flex-wrap: wrap; 
              gap: 8px; 
              margin-top: 2px; 
            }
            .note-row { width: 100%; font-style: italic; color: #1d4ed8; margin-top: 4px; font-size: 10px; }

            .totals-box { 
              display: flex; 
              justify-content: space-between; 
              align-items: center; 
              margin-top: 20px; 
              border: 1.5px solid #000; 
              padding: 8px; 
              background-color: #f8fafc !important;
            }
            .in-words { 
              font-style: italic; 
              font-weight: bold; 
              font-size: 11px; 
              text-transform: uppercase;
              flex: 1;
              padding-right: 15px;
            }
            .grand-total { 
              font-weight: bold; 
              font-size: 14px; 
              border-left: 1.5px solid #000; 
              padding-left: 15px; 
              min-width: 160px; 
              text-align: right;
            }

            /* FIXED SIGNATORY ROW */
            .signatures-row { 
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-top: 80px;
              width: 100%;
              gap: 20px;
            }
            .sig-item {
              flex: 1;
              border-top: 1px solid #000;
              padding-top: 8px;
              font-size: 10px;
              font-weight: bold;
              text-transform: uppercase;
              text-align: center;
              white-space: nowrap;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="text-center">
              <div class="header-title">${settings?.company?.name || ''}</div>
              <div class="header-address">${settings?.company?.address || ''}</div>
              <div class="doc-title">PROJECT REQUISITION</div>
            </div>

            <div class="info-grid">
              <div class="info-col">
                <div><strong>Employee:</strong> ${fullReq.employeeName}</div>
                <div><strong>Designation:</strong> ${fullReq.employeeDesignation || '-'}</div>
                <div><strong>Customer:</strong> ${fullReq.customerName}</div>
                ${fullReq.notes ? `<div><strong>Note:</strong> ${fullReq.notes}</div>` : ''}
              </div>
              <div class="info-col text-right">
                <div><strong>Req #:</strong> ${fullReq.requisitionNumber}</div>
                <div><strong>Date:</strong> ${dateStr}</div>
                <div><strong>Project:</strong> ${fullReq.projectName || '-'}</div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th style="width: 30px;">SL</th>
                  <th style="text-align: left;">Description</th>
                  <th style="width: 70px;">Qty</th>
                  <th style="width: 90px; text-align: right;">Rate</th>
                  <th style="width: 110px; text-align: right;">Total</th>
                </tr>
              </thead>
              <tbody>
                ${fullReq.items.map((it: any, i: number) => {
                  const p = products.find(prod => prod._id === (it.product?._id ?? it.product));
                  const cf = p ? (p as any).customFields : null;
                  const b = it.brand || cf?.brand;
                  const m = it.model || cf?.model;
                  const t = it.type || cf?.type;
                  const ct = it.compressorType || cf?.compressorType;
                  const coo = it.countryOfOrigin || cf?.countryOfOrigin;
                  
                  return `
                    <tr>
                      <td style="text-align: center;">${i + 1}</td>
                      <td>
                        <div class="product-name">${it.name}</div>
                        <div class="product-details">
                          ${b ? `<span>Brand: ${b}</span>` : ''}
                          ${m ? `<span>Model: ${m}</span>` : ''}
                          ${t ? `<span>Type: ${t}</span>` : ''}
                          ${ct ? `<span>Comp: ${ct}</span>` : ''}
                          ${coo ? `<span>Origin: ${coo}</span>` : ''}
                          ${it.remarks ? `<div class="note-row">Note: ${it.remarks}</div>` : ''}
                        </div>
                      </td>
                      <td style="text-align: center;">${it.quantity} ${it.unit}</td>
                      <td style="text-align: right;">${safeCurrency(it.unitPrice)}</td>
                      <td style="text-align: right;" class="font-bold">${safeCurrency(it.quantity * it.unitPrice - (it.discount || 0))}</td>
                    </tr>
                  `}).join('')}
              </tbody>
            </table>

            <div class="totals-box">
              <div class="in-words"><strong>In Words:</strong> ${takaInWords(totals.grandTotal)}</div>
              <div class="grand-total">Grand Total: ${safeCurrency(totals.grandTotal)}</div>
            </div>

            <div class="signatures-row">
              <div class="sig-item">SUBMITTED BY</div>
              <div class="sig-item">VERIFIED BY</div>
              <div class="sig-item">ACCOUNTANT</div>
              <div class="sig-item">MANAGING DIRECTOR</div>
            </div>
          </div>
        </body>
        </html>
      `;
      
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.visibility = 'hidden';
      document.body.appendChild(iframe);
      
      const doc = iframe.contentWindow?.document || iframe.contentDocument;
      if (doc) {
        doc.open('text/html', 'replace');
        doc.write(content);
        doc.close();
        
        setTimeout(() => {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          }
          setTimeout(() => { document.body.removeChild(iframe); }, 1000);
        }, 800);
      }

    } catch (error: any) { ErrorHandler.showError(error); }
  };

  const renderProductDetailsEnhanced = (it: any, p?: Product) => {
    const cf = p ? (p as any).customFields : null;
    const b = it.brand || cf?.brand;
    const m = it.model || cf?.model;
    const t = it.type || cf?.type;
    const ct = it.compressorType || cf?.compressorType;
    const coo = it.countryOfOrigin || cf?.countryOfOrigin;
    return (
      <div className="product-details text-[10px] text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
        {b && <span className="detail-item">Brand: {b}</span>}
        {m && <span className="detail-item">Model: {m}</span>}
        {t && <span className="detail-item">Type: {t}</span>}
        {ct && <span className="detail-item">Comp: {ct}</span>}
        {coo && <span className="detail-item">Origin: {coo}</span>}
        {it.remarks && <div className="w-full italic text-blue-600">Note: {it.remarks}</div>}
      </div>
    );
  };

  const getFilteredProducts = () => {
    if (!productSearchTerm.trim() && !selectedParentCategory && !selectedSubCategory) return [];
    return products.filter(p => {
      const matchesSearch = !productSearchTerm.trim() || p.name.toLowerCase().includes(productSearchTerm.toLowerCase()) || (p.barcode && p.barcode.includes(productSearchTerm));
      const pCatId = typeof p.category === 'object' ? p.category?.parentCategory?._id || p.category?.parentCategory : undefined;
      const sCatId = typeof p.category === 'object' ? p.category?._id : p.category;
      const matchesParent = !selectedParentCategory || pCatId === selectedParentCategory;
      const matchesSub = !selectedSubCategory || sCatId === selectedSubCategory;
      return matchesSearch && matchesParent && matchesSub;
    }).slice(0, 50);
  };

  const clearCurrentProduct = () => {
    setCurrentItem(prev => ({ ...prev, product: undefined, name: '', unit: '', unitPrice: 0, barcode: '', brand: '', model: '', sku: '', type: '', compressorType: '', countryOfOrigin: '', countryOfAssembly: '', details: '', description: '', capacity: '', specification: '', serialNo: '', partNo: '' }));
    setProductSearchTerm('');
  };

  const canEdit = (r: Requisition) => r.status === 'draft' || (user?.role === 'admin');
  const canDelete = (r: Requisition) => user?.role === 'admin';

  const currentTotalVal = (currentItem.quantity || 0) * (currentItem.unitPrice || 0) - (currentItem.discount || 0);
  const currentTotalFormatted = safeCurrency(currentTotalVal);
  const totals = computeRequisitionTotals(addedItems);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Times_New_Roman']">Project Requisitions</h1>
          <p className="text-sm text-[var(--color-text-secondary)]">Manage equipment and material requests</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => fetchRequisitions(true)} className="p-2 border border-[var(--color-border)] rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text)]"><RefreshCw size={20} /></button>
          <Button onClick={openAdd} variant="primary" icon={<Plus size={20} />}>Create Requisition</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-secondary)]" size={18} /><Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search requisitions..." className="pl-10" /></div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg px-3 py-2"><option value="">All Statuses</option><option value="draft">Draft</option><option value="submitted">Submitted</option><option value="verified">Verified</option><option value="approved">Approved</option></select>
      </div>

      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow overflow-hidden">
        <DoubleScrollbar>
          <table className="w-full text-sm text-left"><thead className="bg-[var(--color-background)] text-xs uppercase text-[var(--color-text-secondary)] font-bold border-b border-[var(--color-border)]"><tr><th className="px-6 py-3">Req #</th><th className="px-6 py-3">Date</th><th className="px-6 py-3">Employee</th><th className="px-6 py-3">Customer & Project</th><th className="px-6 py-3">Items</th><th className="px-6 py-3">Status</th><th className="px-6 py-3 text-right">Actions</th></tr></thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {requisitions.filter(r => !searchTerm || r.requisitionNumber.toLowerCase().includes(searchTerm.toLowerCase()) || r.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) || r.customerName.toLowerCase().includes(searchTerm.toLowerCase())).map((r) => (
                <tr key={r._id} className="hover:bg-[var(--color-background-hover)] transition-colors">
                  <td className="px-6 py-4 font-mono font-bold text-blue-600 dark:text-blue-400">{r.requisitionNumber}</td>
                  <td className="px-6 py-4 text-[var(--color-text)]">{formatDateDMY(r.date)}</td>
                  <td className="px-6 py-4"><div className="font-bold text-[var(--color-text)]">{r.employeeName}</div><div className="text-[10px] text-[var(--color-text-secondary)]">{r.employeeDesignation}</div></td>
                  <td className="px-6 py-4"><div className="font-bold text-[var(--color-text)]">{r.customerName}</div><div className="text-[10px] text-[var(--color-text-secondary)]">{r.projectName || '-'}</div></td>
                  <td className="px-6 py-4"><span className="px-2 py-1 bg-[var(--color-background)] border border-[var(--color-border)] rounded-full text-[10px] font-bold uppercase text-[var(--color-text)]">{r.items.length} Product(s)</span></td>
                  <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${r.status === 'approved' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : r.status === 'draft' ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'}`}>{r.status}</span></td>
                  <td className="px-6 py-4"><div className="flex items-center justify-end gap-2"><button onClick={() => openView(r)} className="text-blue-600 dark:text-blue-400 hover:opacity-80" title="View"><Eye size={16}/></button><button onClick={() => openEdit(r)} disabled={!canEdit(r)} className={canEdit(r) ? 'text-amber-600 dark:text-amber-400 hover:opacity-80' : 'text-gray-400 opacity-40'} title="Edit"><Edit size={16}/></button><button onClick={() => handlePrint(r)} className="text-[var(--color-text-secondary)] hover:text-[var(--color-text)]" title="Print"><Printer size={16}/></button><button onClick={() => handleDelete(r)} disabled={!canDelete(r)} className={canDelete(r) ? 'text-red-600 dark:text-red-400 hover:opacity-80' : 'text-gray-400 opacity-40'} title="Delete"><Trash2 size={16}/></button></div></td></tr> ))}
            </tbody></table>
        </DoubleScrollbar>
      </div>

      <Modal isOpen={showAddModal || showEditModal} onClose={closeModals} title={showEditModal ? "Edit Requisition (" + editingRequisition?.requisitionNumber + ")" : 'Create Requisition'} className="!max-w-[98vw] !w-[98vw] !max-h-[98vh] !h-[98vh]">
        <div className="max-w-none w-full mx-auto space-y-6 p-4 md:p-6 text-[var(--color-text)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Date</label><Input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} /></div>
            <div><label className="block text-sm font-medium mb-1">Employee Name</label><Input value={form.employeeName} readOnly className="bg-[var(--color-background)] opacity-80" /></div>
            <div><label className="block text-sm font-medium mb-1">Designation</label><Input value={form.designation} readOnly className="bg-[var(--color-background)] opacity-80" /></div>
            <div>
              <label className="block text-sm font-medium mb-1">Customer Name</label>
              <div className="flex gap-2">
                <select value={form.customerId} onChange={(e) => handleCustomerChange(e.target.value)} className="flex-1 px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg h-10">
                  <option value="">Select Customer</option>
                  {customers.map((c) => (<option key={c._id} value={c._id}>{c.name}</option>))}
                </select>
                <button type="button" onClick={() => setShowCustomerModal(true)} className="h-10 w-10 flex items-center justify-center bg-blue-600 text-white rounded-lg hover:bg-blue-700 shrink-0" title="New Customer"><Plus size={20} /></button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Project Name</label>
              <div className="flex gap-2">
                <select value={form.projectId} onChange={(e) => setForm((p) => ({ ...p, projectId: e.target.value }))} className="flex-1 px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg h-10" disabled={!form.customerId}>
                  <option value="">Select Project</option>
                  {projectsForCustomer.map((p) => (<option key={p._id} value={p._id}>{p.name}</option>))}
                </select>
                <button type="button" onClick={() => setShowProjectModal(true)} disabled={!form.customerId} className="h-10 w-10 flex items-center justify-center bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 shrink-0" title="New Project"><Plus size={20} /></button>
              </div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Note</label><Input value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Requisition Note" /></div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 font-['Times_New_Roman']">Add Product</label>
            <div className="space-y-3 p-3 border border-[var(--color-border)] rounded-lg bg-[var(--color-background)]">
              <div className="flex gap-1"><Input value={currentItem.barcode || ''} onChange={(e) => { const v = e.target.value; setCurrentItem((p) => ({ ...p, barcode: v })); const found = products.find((p: any) => p.barcode === v); if (found) handleProductSelect(found); }} placeholder="Barcode" className="text-xs flex-1" /><button type="button" onClick={() => toast('Scan not implemented')} className="px-2 py-1 bg-blue-600 text-white rounded text-xs">Scan</button></div>
              <select value={selectedParentCategory} onChange={(e) => { setSelectedParentCategory(e.target.value); setSelectedSubCategory(''); }} className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg text-sm"><option value="">Category</option>{parentCategories.map(c => (<option key={c._id} value={c._id}>{c.name}</option>))}</select>
              <select value={selectedSubCategory} onChange={(e) => setSelectedSubCategory(e.target.value)} className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg text-sm" disabled={!selectedParentCategory}><option value="">Sub Category</option>{subCategories.filter((c: any) => (c.parentCategory?._id || c.parentCategory) === selectedParentCategory).map((c: any) => (<option key={c._id} value={c._id}>{c.name}</option>))}</select>
              <div className="relative flex items-center">
                <Input ref={searchInputRef} value={productSearchTerm} onChange={(e) => { setProductSearchTerm(e.target.value); updateDropdownPosition(); setShowProductDropdown(true); }} onFocus={() => { updateDropdownPosition(); setShowProductDropdown(true); }} placeholder="Type to search..." className="w-full text-sm pr-8" />
                {currentItem.product && <button onClick={clearCurrentProduct} className="absolute right-2 text-gray-400 hover:text-red-500 transition-colors"><XCircle size={18} /></button>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {(() => {
                  const p = products.find(prod => prod._id === currentItem.product);
                  const availableUnits = p?.units || (p?.unit ? [p.unit] : []);
                  if (availableUnits.length > 1) {
                    return (
                      <select 
                        value={currentItem.unit} 
                        onChange={(e) => setCurrentItem(prev => ({ ...prev, unit: e.target.value }))}
                        className="w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] rounded-lg text-sm h-10"
                      >
                        {availableUnits.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                    );
                  }
                  return <Input value={currentItem.unit} readOnly placeholder="Unit" className="text-sm bg-[var(--color-background)] opacity-80" />;
                })()}
                <Input type="number" min={1} value={currentItem.quantity} onChange={(e) => setCurrentItem((p) => ({ ...p, quantity: Number(e.target.value) || 0 }))} className="text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3"><Input type="number" min={0} value={currentItem.unitPrice || ''} onChange={(e) => setCurrentItem((p) => ({ ...p, unitPrice: Number(e.target.value) || 0 }))} placeholder="Rate" className="text-sm" /><div className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)] text-sm font-bold text-[var(--color-text)]">{safeCurrency(currentTotalVal)}</div></div>
              <textarea value={currentItem.remarks || ''} onChange={(e) => setCurrentItem((p) => ({ ...p, remarks: e.target.value }))} className="w-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] p-2 rounded text-sm" placeholder="Product Note" rows={2} />
            </div>
            <div className="mt-2 flex gap-2 justify-end"><button onClick={handleAddItem} className="px-4 py-1.5 bg-[var(--color-surface)] border border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg text-sm font-bold">Add Item</button><button onClick={() => setShowAddProductModal(true)} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-bold">New Product</button></div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2 font-['Times_New_Roman']">Added Products</label>
            <DoubleScrollbar>
              <table className="w-full text-sm border-collapse">
                <thead className="bg-[var(--color-background)] text-[var(--color-text-secondary)]">
                  <tr className="border-b border-[var(--color-border)]">
                    <th className="px-2 py-2 text-left">SL</th>
                    <th className="px-2 py-2 text-left">Description</th>
                    <th className="px-2 py-2 text-center w-32">Qty</th>
                    <th className="px-2 py-2 text-right w-40">Rate</th>
                    <th className="px-2 py-2 text-right w-40">Total</th>
                    <th className="px-2 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                  {addedItems.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-2 py-2 text-[var(--color-text)]">{idx+1}</td>
                      <td className="px-2 py-2"><div className="font-bold text-[var(--color-text)]">{item.name}</div>{renderProductDetailsEnhanced(item, products.find(p => p._id === item.product))}</td>
                      <td className="px-2 py-2 text-center font-medium text-[var(--color-text)]">{item.quantity} {item.unit}</td>
                      <td className="px-2 py-2 text-right text-[var(--color-text)]">{safeCurrency(item.unitPrice)}</td>
                      <td className="px-2 py-2 text-right font-bold text-blue-600 dark:text-blue-400">{safeCurrency(item.quantity * item.unitPrice - item.discount)}</td>
                      <td className="px-2 py-2 text-right">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => handleEditItem(idx)} className="text-blue-600 dark:text-blue-400 hover:bg-[var(--color-surface-hover)] p-1 rounded" title="Edit"><Edit size={16}/></button>
                          <button onClick={() => handleRemoveItem(idx)} className="text-red-600 dark:text-red-400 hover:bg-[var(--color-surface-hover)] p-1 rounded" title="Delete"><Trash2 size={16}/></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DoubleScrollbar>
          </div>
          <div className="flex justify-between items-center pt-4 border-t border-[var(--color-border)]"><div className="text-xl font-bold">Total: {safeCurrency(totals.grandTotal)}</div><div className="flex gap-2"><Button onClick={closeModals} variant="outline">Cancel</Button><Button onClick={handleSave} disabled={saving} variant="primary">{saving ? 'Saving...' : showEditModal ? 'Update Requisition' : 'Save Requisition'}</Button></div></div>
        </div>
      </Modal>

      {editProductIdx !== null && editProductData && (
        <Modal isOpen onClose={() => { setEditProductIdx(null); setEditProductData(null); }} title="Edit line" size="lg">
          <div className="space-y-3 p-2 text-[var(--color-text)]">
            <Input label="Product" value={editProductData.name} readOnly className="bg-[var(--color-background)] opacity-80" />
            <div className="grid grid-cols-2 gap-4">
              <Input label="Quantity" type="number" min={1} value={editProductData.quantity} onChange={(e) => setEditProductData((p: any) => (p ? { ...p, quantity: Number(e.target.value) || 0 } : p))} />
              <Input label="Rate" type="number" min={0} value={editProductData.unitPrice || ''} onChange={(e) => setEditProductData((p: any) => (p ? { ...p, unitPrice: Number(e.target.value) || 0 } : p))} />
            </div>
            {(() => {
              const p = products.find(prod => prod._id === editProductData.product);
              const availableUnits = p?.units || (p?.unit ? [p.unit] : []);
              if (availableUnits.length > 1) {
                return (
                  <div className="space-y-1">
                    <label className="text-xs font-bold uppercase text-[var(--color-text-secondary)]">Unit</label>
                    <select 
                      value={editProductData.unit} 
                      onChange={(e) => setEditProductData((prev: any) => ({ ...prev, unit: e.target.value }))}
                      className="w-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] p-2 rounded"
                    >
                      {availableUnits.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                );
              }
              return null;
            })()}
            <textarea value={editProductData.remarks || ''} onChange={(e) => setEditProductData((p: any) => (p ? { ...p, remarks: e.target.value } : p))} className="w-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] p-2 rounded" placeholder="Remarks" rows={2} />
            <div className="flex justify-end gap-3 mt-4">
              <Button onClick={() => { setEditProductIdx(null); setEditProductData(null); }} variant="outline">Cancel</Button>
              <Button onClick={handleSaveEditItem} variant="primary">Save Changes</Button>
            </div>
          </div>
        </Modal>
      )}

      <Modal isOpen={showViewModal} onClose={() => setShowViewModal(false)} title="View Requisition" size="xl">
        {viewRequisition && (
          <div className="p-4 space-y-4 bg-[var(--color-surface)] text-[var(--color-text)]">
            <div className="text-center">
              <h2 className="text-xl font-bold">{settings?.company?.name}</h2>
              <p className="text-sm text-[var(--color-text-secondary)]">{settings?.company?.address}</p>
              <h3 className="text-base font-bold border-y border-[var(--color-border)] py-1 mt-2 uppercase">PROJECT REQUISITION</h3>
            </div>
            <div className="flex justify-between text-sm">
              <div className="space-y-1">
                <div><strong>Employee:</strong> {viewRequisition.employeeName}</div>
                <div><strong>Designation:</strong> {viewRequisition.employeeDesignation || '-'}</div>
                <div><strong>Customer:</strong> {viewRequisition.customerName}</div>
                {viewRequisition.notes && (
                  <div><strong>Note:</strong> {viewRequisition.notes}</div>
                )}
              </div>
              <div className="text-right space-y-1">
                <div><strong>Req #:</strong> {viewRequisition.requisitionNumber}</div>
                <div><strong>Date:</strong> {formatDateDMY(viewRequisition.date)}</div>
                <div><strong>Project:</strong> {viewRequisition.projectName || '-'}</div>
              </div>
            </div>
            <table className="w-full border-collapse border border-[var(--color-border)] text-xs mt-4">
              <thead>
                <tr className="bg-[var(--color-background)] font-bold uppercase text-[var(--color-text-secondary)]">
                  <th className="border border-[var(--color-border)] p-2 w-10">SL</th>
                  <th className="border border-[var(--color-border)] p-2 text-left">Description</th>
                  <th className="border border-[var(--color-border)] p-2 text-center w-24">Qty</th>
                  <th className="border border-[var(--color-border)] p-2 text-right w-28">Rate</th>
                  <th className="border border-[var(--color-border)] p-2 text-right w-32">Total</th>
                </tr>
              </thead>
              <tbody>
                {viewRequisition.items.map((it, i) => (
                  <tr key={i}>
                    <td className="border border-[var(--color-border)] p-2 text-center">{i + 1}</td>
                    <td className="border border-[var(--color-border)] p-2">
                      <div className="font-bold">{it.name}</div>
                      {renderProductDetailsEnhanced(it, products.find(p => p._id === (it.product?._id ?? it.product)))}
                    </td>
                    <td className="border border-[var(--color-border)] p-2 text-center">{it.quantity} {it.unit}</td>
                    <td className="border border-[var(--color-border)] p-2 text-right">{safeCurrency(it.unitPrice)}</td>
                    <td className="border border-[var(--color-border)] p-2 text-right font-bold text-blue-600 dark:text-blue-400">{safeCurrency(it.quantity * it.unitPrice - it.discount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-between items-center mt-6 border-2 border-[var(--color-border)] p-2 bg-[var(--color-background)] leading-tight">
              <div className="italic font-bold text-xs uppercase flex-1 pr-4"><strong>In Words:</strong> {takaInWords(computeRequisitionTotals(viewRequisition.items).grandTotal)}</div>
              <div className="text-right font-bold min-w-[150px] border-l-2 border-[var(--color-border)] pl-4 text-sm">Grand Total: {safeCurrency(computeRequisitionTotals(viewRequisition.items).grandTotal)}</div>
            </div>
            <div className="grid grid-cols-4 gap-4 text-center text-[10px] font-bold" style={{ marginTop: '100px' }}>
              {['SUBMITTED BY', 'VERIFIED BY', 'ACCOUNTANT', 'MANAGING DIRECTOR'].map(s => <div key={s} className="border-t border-[var(--color-border)] pt-2">{s}</div>)}
            </div>
          </div>
        )}
      </Modal>

      {showProductDropdown && createPortal(
        <div ref={dropdownPortalRef} className="fixed z-[10001] bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-2xl max-h-60 overflow-y-auto" style={{ top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownPosition.width }}>{getFilteredProducts().map(p => ( <div key={p._id} onClick={() => handleProductSelect(p)} className="px-4 py-3 border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-surface-hover)] cursor-pointer product-dropdown-item"><div className="font-bold text-sm text-[var(--color-text)]">{p.name}</div>{p.barcode && <div className="text-[10px] text-[var(--color-text-secondary)] font-mono">Barcode: {p.barcode}</div>}</div> ))}{getFilteredProducts().length === 0 && <div className="p-4 text-center text-[var(--color-text-secondary)] text-sm">No products found.</div>}</div>,
        document.body
      )}

      {/* New Product Modal */}
      <Modal isOpen={showAddProductModal} onClose={() => { setShowAddProductModal(false); setSelectedParentCategory(''); setSubCategories([]); setNewProductErrors({}); }} title="New Product" size="xl" className="!max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2 text-[var(--color-text)]">
          <div className="md:col-span-2">
            <Input 
              label="Product Name *" 
              value={newProduct.productName} 
              onChange={e => { setNewProduct({...newProduct, productName: e.target.value}); if(newProductErrors.productName) setNewProductErrors(prev => ({...prev, productName: ''})); }} 
              error={newProductErrors.productName}
              className={newProductErrors.productName ? 'border-red-500 focus:ring-red-500' : ''}
            />
          </div>
          
          <div>
            <label className="block text-xs font-bold uppercase text-[var(--color-text-secondary)] mb-1">Location</label>
            <div className="flex gap-2">
              <select value={newProduct.location} onChange={e => setNewProduct({ ...newProduct, location: e.target.value })} className="flex-1 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] p-2 rounded">
                <option value="">Select Location</option>
                {locations.map(l => <option key={l._id} value={l._id}>{l.name}</option>)}
              </select>
              <button type="button" onClick={() => handleCreateEntity('location')} className="px-2 py-1 bg-blue-600 text-white rounded">Create</button>
            </div>
          </div>

          <div>
            <label className={`block text-xs font-bold uppercase mb-1 ${newProductErrors.parentCategory ? 'text-red-500' : 'text-[var(--color-text-secondary)]'}`}>Parent Category *</label>
            <div className="flex gap-2">
              <select 
                value={selectedParentCategory} 
                onChange={e => { handleParentCategoryChange(e.target.value); if(newProductErrors.parentCategory) setNewProductErrors(prev => ({...prev, parentCategory: ''})); }} 
                className={`flex-1 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] p-2 rounded ${newProductErrors.parentCategory ? 'border-red-500 focus:ring-red-500' : ''}`}
              >
                <option value="">Select Parent Category</option>
                {rootCategories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
              <button type="button" onClick={() => handleCreateEntity('category')} className="px-2 py-1 bg-blue-600 text-white rounded">Create</button>
            </div>
          </div>

          <div>
            <label className={`block text-xs font-bold uppercase mb-1 ${newProductErrors.category ? 'text-red-500' : 'text-[var(--color-text-secondary)]'}`}>Sub Category *</label>
            <select 
              value={newProduct.category} 
              onChange={e => { setNewProduct({ ...newProduct, category: e.target.value }); if(newProductErrors.category) setNewProductErrors(prev => ({...prev, category: ''})); }} 
              className={`w-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] p-2 rounded ${newProductErrors.category ? 'border-red-500 focus:ring-red-500' : ''}`}
            >
              <option value="">Select Sub Category</option>
              {subCategories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase text-[var(--color-text-secondary)] mb-1">Brand</label>
            <div className="flex gap-2">
              <select value={newProduct.brand} onChange={e => updateBarcodeFn({...newProduct, brand: e.target.value})} className="flex-1 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] p-2 rounded">
                <option value="">Select Brand</option>
                {brands.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
              </select>
              <button type="button" onClick={() => handleCreateEntity('brand')} className="px-2 py-1 bg-blue-600 text-white rounded">Create</button>
            </div>
          </div>

          <Input label="Model" value={newProduct.model} onChange={e => updateBarcodeFn({...newProduct, model: e.target.value})} />
          
          <div>
            <label className={`block text-xs font-bold uppercase mb-1 ${newProductErrors.unitIds ? 'text-red-500' : 'text-[var(--color-text-secondary)]'}`}>Units *</label>
            <div className="relative" ref={unitDropdownRef}>
              <button 
                type="button" 
                onClick={() => setUnitDropdownOpen(!unitDropdownOpen)} 
                className={`w-full border border-[var(--color-border)] p-2 rounded text-left flex justify-between items-center bg-[var(--color-surface)] text-[var(--color-text)] ${newProductErrors.unitIds ? 'border-red-500 focus:ring-red-500' : ''}`}
              >
                <span className="truncate">{newProduct.unitIds.length > 0 ? `${newProduct.unitIds.length} units selected` : 'Select Units'}</span>
                <ChevronDown size={16} />
              </button>
              {unitDropdownOpen && (
                <div className="absolute z-20 mt-1 w-full bg-[var(--color-surface)] border border-[var(--color-border)] rounded shadow-lg max-h-40 overflow-y-auto">
                  {units.map(u => (
                    <label key={u._id} className="flex items-center px-3 py-2 hover:bg-[var(--color-surface-hover)] cursor-pointer text-[var(--color-text)]">
                      <input type="checkbox" checked={newProduct.unitIds.includes(u._id)} onChange={() => {
                        const ids = newProduct.unitIds.includes(u._id) ? newProduct.unitIds.filter(id => id !== u._id) : [...newProduct.unitIds, u._id];
                        setNewProduct({...newProduct, unitIds: ids});
                        if(newProductErrors.unitIds) setNewProductErrors(prev => ({...prev, unitIds: ''}));
                      }} className="mr-2" />
                      <span className="text-sm">{u.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Input label="SKU" value={newProduct.sku} onChange={e => setNewProduct({...newProduct, sku: e.target.value})} />
          
          {isAirConditioner && (
            <div>
              <label className="block text-xs font-bold uppercase text-[var(--color-text-secondary)] mb-1">Type</label>
              <div className="flex gap-2">
                <select value={newProduct.type} onChange={e => updateBarcodeFn({...newProduct, type: e.target.value})} className="flex-1 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] p-2 rounded">
                  <option value="">Select Type</option>
                  {types.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
                <button type="button" onClick={() => handleCreateEntity('type')} className="px-2 py-1 bg-blue-600 text-white rounded">Create</button>
              </div>
            </div>
          )}

          {isAirConditioner && (
            <div>
              <label className="block text-xs font-bold uppercase text-[var(--color-text-secondary)] mb-1">Compressor Type</label>
              <div className="flex gap-2">
                <select value={newProduct.compressorType} onChange={e => updateBarcodeFn({...newProduct, compressorType: e.target.value})} className="flex-1 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] p-2 rounded">
                  <option value="">Select Compressor Type</option>
                  {compressorTypes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
                <button type="button" onClick={() => handleCreateEntity('compressorType')} className="px-2 py-1 bg-blue-600 text-white rounded">Create</button>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold uppercase text-[var(--color-text-secondary)] mb-1">Country of Origin</label>
            <div className="flex gap-2">
              <select value={newProduct.countryOfOrigin} onChange={e => setNewProduct({...newProduct, countryOfOrigin: e.target.value})} className="flex-1 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] p-2 rounded">
                <option value="">Select Country</option>
                {countries.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
              <button type="button" onClick={() => handleCreateEntity('country')} className="px-2 py-1 bg-blue-600 text-white rounded">Create</button>
            </div>
          </div>

          {isAirConditioner && (
            <div>
              <label className="block text-xs font-bold uppercase text-[var(--color-text-secondary)] mb-1">Country of Assembly</label>
              <div className="flex gap-2">
                <select value={newProduct.countryOfAssembly} onChange={e => setNewProduct({...newProduct, countryOfAssembly: e.target.value})} className="flex-1 border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] p-2 rounded">
                  <option value="">Select Country</option>
                  {countries.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
                <button type="button" onClick={() => handleCreateEntity('country')} className="px-2 py-1 bg-blue-600 text-white rounded">Create</button>
              </div>
            </div>
          )}

          {!isAirConditioner && <Input label="Specifications" value={newProduct.specification} onChange={e => setNewProduct({...newProduct, specification: e.target.value})} />}
          {!isAirConditioner && <Input label="Serial No." value={newProduct.serialNo} onChange={e => setNewProduct({...newProduct, serialNo: e.target.value})} />}
          {!isAirConditioner && <Input label="Part No." value={newProduct.partNo} onChange={e => setNewProduct({...newProduct, partNo: e.target.value})} />}
          
          <Input label="Price" type="number" value={newProduct.price} onChange={e => setNewProduct({...newProduct, price: e.target.value})} />
          
          <div className="md:col-span-2 border border-[var(--color-border)] p-4 rounded bg-[var(--color-background)]">
            <canvas ref={barcodeCanvasRef} style={{display:'none'}} />
            <div className="text-center mt-2 font-mono font-bold text-xs uppercase tracking-widest text-[var(--color-text)]">{newProduct.generatedBarcode || 'BARCODE-PREVIEW'}</div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <Button onClick={() => setShowAddProductModal(false)} variant="outline">Cancel</Button>
          <Button onClick={handleAddNewProduct} variant="primary">Create & Select Product</Button>
        </div>
      </Modal>

      {/* Create Entity Modal (Location, Category, Brand, etc.) */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title={`Create ${createModalType}`} size="md">
        <div className="space-y-4 text-[var(--color-text)]">
          <Input label="Name" value={createModalData.name} onChange={e => setCreateModalData({...createModalData, name: e.target.value})} />
          {(createModalType === 'location' || createModalType === 'category' || createModalType === 'brand' || createModalType === 'type' || createModalType === 'compressorType' || createModalType === 'country' || createModalType === 'unit') && (
            <textarea className="w-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] p-2 rounded" placeholder="Description" rows={3} value={createModalData.description} onChange={e => setCreateModalData({...createModalData, description: e.target.value})} />
          )}
          {createModalType === 'brand' && <Input label="Website" value={createModalData.website} onChange={e => setCreateModalData({...createModalData, website: e.target.value})} />}
          {createModalType === 'country' && (
            <div className="grid grid-cols-2 gap-4">
              <Input label="Code" value={createModalData.code} onChange={e => setCreateModalData({...createModalData, code: e.target.value})} />
              <Input label="Region" value={createModalData.region} onChange={e => setCreateModalData({...createModalData, region: e.target.value})} />
            </div>
          )}
          <div className="flex justify-end gap-3 mt-6">
            <Button onClick={() => setShowCreateModal(false)} variant="outline">Cancel</Button>
            <Button onClick={handleCreateEntitySubmit} variant="primary">Create</Button>
          </div>
        </div>
      </Modal>

      {/* New Customer Modal */}
      <Modal isOpen={showCustomerModal} onClose={() => setShowCustomerModal(false)} title="New Customer" size="md">
        <div className="space-y-4 text-[var(--color-text)]">
          <Input label="Customer Name" value={newCustomer.name} onChange={e => setNewCustomer({...newCustomer, name: e.target.value})} />
          <Input label="Address" value={newCustomer.address} onChange={e => setNewCustomer({...newCustomer, address: e.target.value})} />
          <div className="flex justify-end gap-3 mt-6">
            <Button onClick={() => setShowCustomerModal(false)} variant="outline">Cancel</Button>
            <Button onClick={handleCreateCustomer} variant="primary">Create Customer</Button>
          </div>
        </div>
      </Modal>

      {/* New Project Modal */}
      <Modal isOpen={showProjectModal} onClose={() => setShowProjectModal(false)} title="New Project" size="md">
        <div className="space-y-4 text-[var(--color-text)]">
          <Input label="Project Name" value={newProject.name} onChange={e => setNewProject({...newProject, name: e.target.value})} />
          <div className="space-y-1">
            <label className="text-xs font-bold uppercase text-[var(--color-text-secondary)]">Customer</label>
            <Input value={customers.find(c => c._id === form.customerId)?.name || ''} readOnly className="bg-[var(--color-background)] opacity-80" />
          </div>
          <textarea className="w-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] p-2 rounded" placeholder="Project Description" rows={3} value={newProject.description} onChange={e => setNewProject({...newProject, description: e.target.value})} />
          <div className="flex justify-end gap-3 mt-6">
            <Button onClick={() => setShowProjectModal(false)} variant="outline">Cancel</Button>
            <Button onClick={handleCreateProject} variant="primary">Create Project</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
export default RequisitionsPage;
